import { JSX, useMemo } from "react";
import dayjs from "dayjs";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { TableUI } from "components/design/TableUI";
import {
  Accordion,
  ItemGrid,
} from "components/DesignSystem/Accordion/Accordion";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Breadcrumb from "components/DesignSystem/Breadcrumb/Breadcrumb";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import { Link } from "components/DesignSystem/Link/Link";
import Slider from "components/DesignSystem/Slider/Slider";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { MMM_APOSTROPHE_YY } from "constants/date";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useGetEntityInvoiceQuery } from "store/apis/invoices";
import { RecognitionWaterfallKind } from "store/apis/recognitionWaterfall";
import { useGetBillOrRequestQuery } from "store/apis/vendorBills";
import { InvoiceItem } from "types/Models/invoice";
import { capitalizeFirstLetter } from "utils/capitalizeFirstLetter";
import { EXPENSE_KIND, LABELS } from "./constants";

export type SelectedItem = {
  itemId: string;
  documentId: string;
  documentNumber: string;
  description: string;
};

type ItemDetailsProps = {
  kind: RecognitionWaterfallKind;
  partyName: string;
  // Title of the cell slider, shown as the breadcrumb back to it
  header: string;
  item: SelectedItem;
};

type ScheduleRow = {
  period: string;
  periodStart: string;
  periodEnd: string;
  amount: number;
  status: string;
};

const RECOGNIZED_STATUS = "RECOGNIZED";

// Invoices carry the per-item schedule as revenue_schedule, bills as expense_schedule
const toScheduleRows = (item?: InvoiceItem): ScheduleRow[] =>
  (item?.revenue_schedule || item?.expense_schedule || []).map((entry) => ({
    period: entry.period,
    periodStart: entry.period_start,
    periodEnd: entry.period_end,
    amount: Number(entry.amount),
    status: entry.status,
  }));

// The same document fetch the invoice and bill sliders use; only one runs per kind
const useDocumentItem = (
  kind: RecognitionWaterfallKind,
  item: SelectedItem
) => {
  const groupId = useCurrentGroupContext().uuid;
  const entityId = useCurrentEntityId();
  const isBill = kind === EXPENSE_KIND;
  const invoice = useGetEntityInvoiceQuery(
    { groupId, entityId, invoiceId: item.documentId },
    { skip: isBill || !groupId || !entityId }
  );
  const bill = useGetBillOrRequestQuery(
    { groupId, entityId, billId: item.documentId },
    { skip: !isBill || !groupId || !entityId }
  );
  const { data, isLoading, isSuccess, isError } = isBill ? bill : invoice;
  const documentItem = data?.invoice_items.find(
    (invoiceItem) => invoiceItem.uuid === item.itemId
  );
  return { documentItem, isLoading, isSuccess, isError };
};

const columnHelper = createColumnHelper<ScheduleRow>();

const columns = [
  columnHelper.accessor("period", {
    header: "Period",
    cell: (info) => info.getValue(),
    size: 25,
  }),
  columnHelper.accessor("amount", {
    header: () => <div className="t-text-right">Recognition Amount</div>,
    cell: (info) => (
      <div className="t-text-right">
        <AmountSuperScript amount={info.getValue()} />
      </div>
    ),
    size: 40,
  }),
  columnHelper.accessor("status", {
    header: () => <div className="t-text-right">Status</div>,
    cell: (info) => (
      <div className="t-text-right">
        <Tag
          transparent
          tagType={info.getValue() === RECOGNIZED_STATUS ? "green" : "yellow"}
        >
          {capitalizeFirstLetter(info.getValue().toLowerCase())}
        </Tag>
      </div>
    ),
    size: 35,
  }),
];

// Mirrors the Revenue/Expense Waterfall item slider: breadcrumb back to the cell, details, then the schedule
export const ItemDetails = ({
  kind,
  partyName,
  header,
  item,
}: ItemDetailsProps): JSX.Element => {
  const labels = LABELS[kind];
  const { link } = useConstructInternalLink();
  const { documentItem, isLoading, isSuccess, isError } = useDocumentItem(
    kind,
    item
  );
  const schedule = useMemo(() => toScheduleRows(documentItem), [documentItem]);

  const table = useReactTable({
    data: schedule,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <Slider.Header>
        <Slider.Title>
          <div className="t-flex t-flex-col t-gap-1">
            <Breadcrumb.Root>
              <Slider.RawClose asChild>
                <Breadcrumb.Item as="button">{header}</Breadcrumb.Item>
              </Slider.RawClose>
            </Breadcrumb.Root>
            {item.description} ({item.documentNumber})
          </div>
        </Slider.Title>
        <Slider.Close />
      </Slider.Header>
      <Slider.Body className="t-flex t-flex-col t-gap-4 t-text-text-60">
        <Async.Root
          isLoading={isLoading}
          isSuccess={isSuccess}
          isError={isError}
          // Without schedule rows there is no recognition period to show
          isEmpty={!documentItem || schedule.length === 0}
        >
          <Async.ErrorHandler>
            <div className="t-text-center t-py-10 t-text-red">
              Couldn't load the schedule. Please try again.
            </div>
          </Async.ErrorHandler>
          <Async.Empty>
            <div className="t-text-center t-py-10 t-text-text-30">
              Schedule not found
            </div>
          </Async.Empty>
          <Async.Success>
            <Accordion.Root
              defaultValue="item-details"
              type="single"
              collapsible
            >
              <Accordion.Item value="item-details">
                <Accordion.Trigger disabled>
                  <InfoItem label="Amount Booked">
                    <AmountSuperScript
                      amount={Number(documentItem?.amount || 0)}
                    />
                  </InfoItem>
                </Accordion.Trigger>
                <Accordion.Content>
                  <ItemGrid>
                    <InfoItem label={labels.party}>{partyName}</InfoItem>
                    <InfoItem label="Category">{labels.category}</InfoItem>
                    <InfoItem label="Recognition Period">
                      {dayjs(schedule.at(0)?.periodStart).format(
                        MMM_APOSTROPHE_YY
                      )}{" "}
                      -{" "}
                      {dayjs(schedule.at(-1)?.periodEnd).format(
                        MMM_APOSTROPHE_YY
                      )}
                    </InfoItem>
                    <InfoItem label={labels.linkedDocument}>
                      <Link to={link(labels.documentPath(item.documentId))}>
                        {item.documentNumber}
                      </Link>
                    </InfoItem>
                  </ItemGrid>
                </Accordion.Content>
              </Accordion.Item>
            </Accordion.Root>
            <TableUI table={table} size="regular" />
          </Async.Success>
        </Async.Root>
      </Slider.Body>
    </>
  );
};
